import React, { useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import * as Yup from "yup";
import { ErrorMessage, Field, Form, Formik, useFormikContext } from "formik";

import {
  Button,
  TextField as MuiTextField,
  Alert,
  AlertTitle,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import { Box, spacing } from "@mui/system";

import useAuth from "../../hooks/useAuth";
import { getRoadInfo, roadLookupError } from "../../services/vayvaService";
import SignUpRoadMap, { PickedRoad } from "./SignUpRoadMap";
import { SUPPORT_PATH } from "../../utils/support";
import {
  trackSignUpViewed,
  trackSignUpCompleted,
} from "../../analytics/signupTracking";

const UserRoot = styled.div<{ wide?: boolean }>`
  /*
   * The wrapping Paper is shrink-to-fit inside a flex column, so it has no
   * definite width for a percentage to resolve against — the map step needs
   * an explicit width to widen the card, not "width: 100%".
   */
  max-width: ${(props) => (props.wide ? "100%" : "540px")};
  width: ${(props) => (props.wide ? "min(860px, 82vw)" : "100%")};
  margin: 0 auto;
  justify-content: center;
  align-items: center;
  display: flex;
  min-height: 100%;
  flex-direction: column;
`;

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

interface Step1Props {
  currentStep: number;
}

const supportLink = (
  <MuiLink component={RouterLink} to={SUPPORT_PATH}>
    ota yhteyttä tukeen
  </MuiLink>
);

const ErrorAutoClear: React.FC<{ onClear: () => void }> = ({ onClear }) => {
  const { values } = useFormikContext<any>();
  const prev = React.useRef(values);

  React.useEffect(() => {
    if (prev.current !== values) {
      onClear();
      prev.current = values;
    }
  }, [values, onClear]);

  return null;
};

const makeError = (title: string, body: string) => (
  <>
    <AlertTitle>{title}</AlertTitle>
    {body} Voit myös {supportLink}.
  </>
);

const ERRORS = {
  roadNotPicked: makeError(
    "Tietä ei ole valittu",
    "Valitse tiesi kartalta ennen lähettämistä."
  ),
  roadExists: makeError(
    "Tie on jo käytössä",
    "Jos sinun tulisi hallinnoida tätä tietä, ole yhteydessä tukeen."
  ),
  userExists: makeError(
    "Sähköpostiosoite on jo rekisteröity",
    "Voit kirjautua sisään tai palauttaa salasanan."
  ),
  server: makeError("Järjestelmävirhe", "Yritä hetken kuluttua uudelleen."),
  network: makeError(
    "Rekisteröinti epäonnistui",
    "Tarkista verkkoyhteytesi ja yritä uudelleen."
  ),
};

export const Step1: React.FC<Step1Props> = ({ currentStep }) => {
  if (currentStep !== 1) return null;

  return (
    <>
      <Field name="firstName" my={3}>
        {({ field, form }: any) => (
          <TextField
            {...field}
            label="Etunimi"
            variant="outlined"
            fullWidth
            error={!!form.errors.firstName && form.touched.firstName}
            helperText={<ErrorMessage name="firstName" />}
            my={2}
          />
        )}
      </Field>
      <Field name="lastName">
        {({ field, form }: any) => (
          <TextField
            {...field}
            label="Sukunimi"
            variant="outlined"
            fullWidth
            error={!!form.errors.lastName && form.touched.lastName}
            helperText={<ErrorMessage name="lastName" />}
            my={2}
          />
        )}
      </Field>
      <Field name="email">
        {({ field, form }: any) => (
          <TextField
            {...field}
            label="Sähköposti"
            variant="outlined"
            fullWidth
            error={!!form.errors.email && form.touched.email}
            helperText={<ErrorMessage name="email" />}
            my={2}
          />
        )}
      </Field>
      <Field name="phone">
        {({ field, form }: any) => (
          <TextField
            {...field}
            label="Puhelinnumero"
            variant="outlined"
            fullWidth
            error={!!form.errors.phone && form.touched.phone}
            helperText={<ErrorMessage name="phone" />}
            my={2}
          />
        )}
      </Field>
      <Field name="password">
        {({ field, form }: any) => (
          <TextField
            {...field}
            label="Salasana"
            variant="outlined"
            type="password"
            fullWidth
            error={!!form.errors.password && form.touched.password}
            helperText={<ErrorMessage name="password" />}
            my={2}
          />
        )}
      </Field>
      <Field name="passwordConfirmation">
        {({ field, form }: any) => (
          <TextField
            {...field}
            label="Vahvista salasana"
            variant="outlined"
            type="password"
            fullWidth
            error={
              !!form.errors.passwordConfirmation &&
              form.touched.passwordConfirmation
            }
            helperText={<ErrorMessage name="passwordConfirmation" />}
            my={2}
          />
        )}
      </Field>
      <Field name="termsAccepted" type="checkbox">
        {({ field, form }: any) => (
          <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
            <label>
              <input
                {...field}
                type="checkbox"
                checked={field.value}
                style={{ marginRight: "0.5rem" }}
              />
              Hyväksyn{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "underline" }}
              >
                käyttöehdot ja tietosuojaselosteen
              </a>
            </label>
            {form.errors.termsAccepted && form.touched.termsAccepted && (
              <p style={{ color: "#d32f2f", fontSize: "0.8rem" }}>
                {form.errors.termsAccepted}
              </p>
            )}
          </div>
        )}
      </Field>
    </>
  );
};

interface Step2Props {
  currentStep: number;
}

export const Step2: React.FC<Step2Props> = ({ currentStep }) => {
  if (currentStep !== 2) return null;

  return (
    <>
      <Field name="streetName" my={3}>
        {({ field, form }: any) => (
          <TextField
            {...field}
            label="Tien nimi"
            variant="outlined"
            fullWidth
            error={!!form.errors.streetName && form.touched.streetName}
            helperText={<ErrorMessage name="streetName" />}
            my={2}
          />
        )}
      </Field>
      <Field name="municipality">
        {({ field, form }: any) => (
          <TextField
            {...field}
            label="Kunta"
            variant="outlined"
            fullWidth
            error={!!form.errors.municipality && form.touched.municipality}
            helperText={<ErrorMessage name="municipality" />}
            my={2}
          />
        )}
      </Field>
    </>
  );
};

interface Step3Props {
  currentStep: number;
  picked: PickedRoad | null;
  onRoadPicked: (road: PickedRoad | null) => void;
  /** The register's own words on why the typed name did not work. */
  notice?: string | null;
}

/** "(3 osaa, 1441 m)" — omits whichever half it does not have. */
const describeRoute = (road: PickedRoad): string => {
  const bits: string[] = [];
  if (road.parts && road.parts.length > 1)
    bits.push(`${road.parts.length} osaa`);
  if (road.lengthMeters) bits.push(`${road.lengthMeters} m`);
  return bits.length ? ` (${bits.join(", ")})` : "";
};

export const Step3: React.FC<Step3Props> = ({
  currentStep,
  picked,
  onRoadPicked,
  notice,
}) => {
  const { values, setFieldValue } = useFormikContext<any>();

  if (currentStep !== 3) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Valitse tiesi kartalta
      </Typography>
      {notice && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Tierekisteri ei pysty palauttamaan tämän tien reittiä: {notice}{" "}
          Valitse tie kartalta tai piirrä se itse.
        </Alert>
      )}
      <SignUpRoadMap
        municipality={values.municipality}
        picked={picked}
        onRoadPicked={(road) => {
          onRoadPicked(road);
          // A register-resolved road hands back its own spelling, and writing
          // that in is what makes the name lookup succeed on submit. A road
          // the register cannot name leaves the field for the user to fill.
          if (!road?.needsName) {
            setFieldValue("streetName", road?.name ?? "");
          }
          setFieldValue(
            "municipality",
            road?.municipality || values.municipality
          );
        }}
      />

      {picked?.needsName && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Tierekisterissä ei ole nimeä tälle tielle. Anna tiekuntasi käyttämä
            nimi, niin tallennamme sen valitsemallasi reitillä
            {describeRoute(picked)}.
          </Alert>
          <Field name="streetName">
            {({ field, form }: any) => (
              <TextField
                {...field}
                label="Tien nimi"
                variant="outlined"
                fullWidth
                error={!!form.errors.streetName && form.touched.streetName}
                helperText={<ErrorMessage name="streetName" />}
                my={2}
              />
            )}
          </Field>
          <Field name="municipality">
            {({ field, form }: any) => (
              <TextField
                {...field}
                label="Kunta"
                variant="outlined"
                fullWidth
                error={!!form.errors.municipality && form.touched.municipality}
                helperText={<ErrorMessage name="municipality" />}
                my={2}
              />
            )}
          </Field>
        </Box>
      )}
    </Box>
  );
};

function SignUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signUp } = useAuth();

  const [success, setSuccess] = useState<boolean>(false);
  const [membership, setMembership] = useState<string>("basic");
  const [error, setError] = useState<React.ReactNode>(null);

  // Track when sign-up form is viewed
  useEffect(() => {
    trackSignUpViewed();
  }, []);

  const validationSchema = [
    Yup.object({
      firstName: Yup.string().trim().required("Etunimi on pakollinen"),
      lastName: Yup.string().trim().required("Sukunimi on pakollinen"),
      email: Yup.string()
        .trim()
        .email("Virheellinen sähköpostiosoite")
        .required("Sähköposti on pakollinen"),
      phone: Yup.string()
        .trim()
        .matches(/^\+?[0-9\s\-()]{7,20}$/, "Virheellinen puhelinnumero")
        .required("Puhelinnumero on pakollinen"),
      password: Yup.string()
        .min(6, "Salasanan tulee olla vähintään 6 merkkiä pitkä")
        .required("Salasana on pakollinen"),
      passwordConfirmation: Yup.string()
        .oneOf([Yup.ref("password"), null], "Salasanat eivät täsmää")
        .required("Salasanan vahvistaminen on pakollinen"),
      termsAccepted: Yup.boolean()
        .oneOf([true], "Sinun tulee hyväksyä käyttöehdot ja tietosuojaseloste")
        .required("Sinun tulee hyväksyä käyttöehdot ja tietosuojaseloste"),
    }),
    Yup.object({
      streetName: Yup.string().trim().required("Tien nimi on pakollinen"),
      municipality: Yup.string().trim().required("Kunta on pakollinen"),
    }),
    // Step 3 writes the same two fields from the map, so it reuses the shape.
    Yup.object({
      streetName: Yup.string().trim().required("Valitse tie kartalta"),
      municipality: Yup.string().trim().required("Valitse tie kartalta"),
    }),
  ];

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    passwordConfirmation: "",
    streetName: "",
    municipality: "",
    termsAccepted: false,
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [pickedRoad, setPickedRoad] = useState<PickedRoad | null>(null);
  const [stepTwoNotice, setStepTwoNotice] = useState<string | null>(null);

  // Step 2 submits for real when the name lookup hits, so it is a "Lähetä"
  // too — step 3 only appears when that lookup misses.
  const isLastStep = currentStep >= 2;

  /**
   * Registers with the road register's own spelling of the name rather than
   * whatever the user typed. The backend looks the road up again by that
   * exact string, and it names the association after it.
   */
  const submitRegistration = async (
    values: any,
    streetName: string,
    municipality: string,
    road?: PickedRoad | null
  ) => {
    await signUp({
      email: values.email?.trim() || "",
      password: values.password,
      firstName: values.firstName?.trim() || "",
      lastName: values.lastName?.trim() || "",
      phone: values.phone?.trim() || "",
      streetName,
      municipality,
      membership: membership,
      // Only for roads the register could not resolve; the backend looks the
      // geometry up itself for everything else.
      ...(road?.parts
        ? { roadParts: road.parts, roadSource: road.source }
        : {}),
    });
    trackSignUpCompleted();
    setSuccess(true);
  };

  const handleSubmit = async (values: any, actions: any) => {
    try {
      if (currentStep === 1) {
        setCurrentStep(2);
        return;
      }

      if (currentStep === 3) {
        if (!pickedRoad) {
          setError(ERRORS.roadNotPicked);
          return;
        }
        // A road the register could not name is named by the user, so take
        // the typed values rather than the empty ones the map reported.
        await submitRegistration(
          values,
          pickedRoad.needsName
            ? values.streetName?.trim() || ""
            : pickedRoad.name,
          pickedRoad.needsName
            ? values.municipality?.trim() || ""
            : pickedRoad.municipality,
          pickedRoad
        );
        return;
      }

      // Step 2: the road register only matches names exactly, so a miss here
      // is usually a spelling slip rather than an unknown road. Hand the user
      // the map instead of a dead end.
      const streetName = values.streetName?.trim() || "";
      const municipality = values.municipality?.trim() || "";

      const res = await getRoadInfo(streetName, municipality);
      const feature = res.data?.features?.[0];
      const lookupError = roadLookupError(res.data);

      if (lookupError || !feature?.properties?.katunimi) {
        // Carry the register's own wording across. A discontiguous street
        // ("Kadun geometria ei ole yhtenäinen") is not a spelling problem, and
        // arriving at the map with no explanation invites the user to retype
        // the same name and land right back here.
        setStepTwoNotice(
          lookupError && !lookupError.startsWith("Annetuilla parametreilla")
            ? lookupError
            : null
        );
        setCurrentStep(3);
        return;
      }

      await submitRegistration(
        values,
        feature.properties.katunimi,
        feature.properties.kuntanimi
      );
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;

      if (typeof errorMessage === "string") {
        if (errorMessage.includes("Road already exists")) {
          setError(ERRORS.roadExists);
        } else if (errorMessage.includes("User already exists")) {
          setError(ERRORS.userExists);
        } else {
          setError(ERRORS.server);
        }
      } else {
        setError(ERRORS.network);
      }
    } finally {
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  };

  const prevStep = () => {
    if (currentStep === 3) {
      setPickedRoad(null);
      setStepTwoNotice(null);
    }
    setCurrentStep((prevStep) => prevStep - 1);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const membershipParam = params.get("membership");
    setMembership(membershipParam || "basic");
  }, [location.search]);

  if (success) {
    return (
      <>
        <p>Aktivoi tunnuksesi sähköpostista löytyvästä linkistä.</p>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            pt: 2,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/")}
            sx={{ mr: 1 }}
          >
            Kirjaudu
          </Button>
        </Box>
      </>
    );
  } else {
    return (
      <UserRoot wide={currentStep === 3}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema[currentStep - 1]}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form style={{ width: "100%" }}>
              <ErrorAutoClear onClear={() => error && setError(null)} />
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Step1 currentStep={currentStep} />
              <Step2 currentStep={currentStep} />
              <Step3
                currentStep={currentStep}
                picked={pickedRoad}
                onRoadPicked={setPickedRoad}
                notice={stepTwoNotice}
              />

              <div>
                <Button
                  type={"submit"}
                  disabled={
                    isSubmitting ||
                    (currentStep === 3 &&
                      (!pickedRoad || pickedRoad.parts?.length === 0))
                  }
                  variant="contained"
                  color="primary"
                  fullWidth
                >
                  {isLastStep ? "Lähetä" : "Seuraava"}
                </Button>
                {currentStep === 2 && (
                  <Box sx={{ textAlign: "center", mt: 1.5 }}>
                    <MuiLink
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => setCurrentStep(3)}
                    >
                      En löydä tietäni — valitse se kartalta
                    </MuiLink>
                  </Box>
                )}
                {currentStep > 1 && (
                  <Button
                    style={{ marginRight: "8px" }}
                    type={"button"}
                    color="primary"
                    onClick={prevStep}
                    fullWidth
                  >
                    Takaisin
                  </Button>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </UserRoot>
    );
  }
}

export default SignUp;
